var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { NetworkStatus, useMutation, useQuery } from "@apollo/client";
import { AuthContext } from "@/providers/AuthContextProvider";
import { useTenantStore } from "@/providers/TenantContextProvider";
import { IPermissions } from "@/types/keycloak";
import { getGraphQLActionErrorReason } from "@/services/graphqlActionError";
import { demoLabel } from "./messageContent";
import { FAKE_PROVIDER_GET_STARTED, FAKE_PROVIDER_MESSAGE, FAKE_PROVIDER_MESSAGES } from "./queries";
// Both actions are permitted to messaging-account-write only.
var WRITE_ROLE = { headers: { "x-hasura-role": IPermissions.MESSAGING_ACCOUNT_WRITE } };
var NO_MESSAGES = [];
/**
 * What the dev fake provider received. Read once to decide whether the Sent
 * messages tab exists, then every `refreshMs` while `active` (0: never, as in
 * stories), and only once it has answered: a deep link to the tab on a
 * deployment without the routes never polls a failing action (R6-05).
 */
export var useSentMessages = function (_a) {
    var _b;
    var active = _a.active, refreshMs = _a.refreshMs;
    var authContext = useContext(AuthContext);
    var tenantId = useTenantStore()[0];
    var canWrite = authContext.isAuthorized(true, tenantId, IPermissions.MESSAGING_ACCOUNT_WRITE);
    // Set from the answer of a previous render: the first read never polls.
    var _c = useState(false), answered = _c[0], setAnswered = _c[1];
    var _d = useQuery(FAKE_PROVIDER_MESSAGES, {
        skip: !canWrite || !tenantId,
        pollInterval: active && answered && refreshMs > 0 ? refreshMs : 0,
        fetchPolicy: "network-only",
        notifyOnNetworkStatusChange: true,
        context: WRITE_ROLE,
    }), data = _d.data, previousData = _d.previousData, error = _d.error, networkStatus = _d.networkStatus, refetch = _d.refetch;
    // After a failed refresh Apollo clears `data`; `previousData` keeps the last answer.
    var list = (_b = data === null || data === void 0 ? void 0 : data.messaging_fake_provider_messages) !== null && _b !== void 0 ? _b : previousData === null || previousData === void 0 ? void 0 : previousData.messaging_fake_provider_messages;
    var messages = useMemo(function () {
        var _a;
        return (_a = list === null || list === void 0 ? void 0 : list.messages.map(function (message) { return (__assign(__assign({}, message), { sender: demoLabel(message.sender), recipient: demoLabel(message.recipient) })); })) !== null && _a !== void 0 ? _a : NO_MESSAGES;
    }, [list]);
    useEffect(function () { return setAnswered(!!list); }, [list]);
    var refresh = useCallback(function () {
        refetch().catch(function () { return undefined; });
    }, [refetch]);
    return {
        available: !!list,
        messages: messages,
        error: list ? error : undefined,
        refreshing: networkStatus === NetworkStatus.refetch,
        refresh: refresh,
    };
};
/** Get Started for a pasted m.me link; a refused request comes back as its reason. */
export var useFakeGetStarted = function () {
    var getStarted = useMutation(FAKE_PROVIDER_GET_STARTED, {
        context: WRITE_ROLE,
    })[0];
    return useCallback(function (link) { return __awaiter(void 0, void 0, void 0, function () {
        var data, error_1;
        var _a, _b;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    _c.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, getStarted({ variables: { link: link } })];
                case 1:
                    data = (_c.sent()).data;
                    return [2 /*return*/, (_a = data === null || data === void 0 ? void 0 : data.messaging_fake_provider_get_started) !== null && _a !== void 0 ? _a : { status: null, reason: null }];
                case 2:
                    error_1 = _c.sent();
                    // A refused link (no ref, an unknown Page) comes back as the action's error.
                    return [2 /*return*/, { status: null, reason: (_b = getGraphQLActionErrorReason(error_1)) !== null && _b !== void 0 ? _b : null }];
                case 3: return [2 /*return*/];
            }
        });
    }); }, [getStarted]);
};
/**
 * The HTML of the email open in the reading pane, read on its own: the list
 * leaves it out (R6-03). Not cached, so the list's polls never drop it.
 */
export var useFakeEmailHtml = function (id, hasHtml) {
    var _a;
    var _b = useQuery(FAKE_PROVIDER_MESSAGE, {
        variables: { id: id },
        skip: !hasHtml,
        fetchPolicy: "no-cache",
        context: WRITE_ROLE,
    }), data = _b.data, loading = _b.loading, error = _b.error;
    var entry = data === null || data === void 0 ? void 0 : data.messaging_fake_provider_message;
    return {
        html: (_a = entry === null || entry === void 0 ? void 0 : entry.content.html) !== null && _a !== void 0 ? _a : null,
        loading: hasHtml && loading,
        failed: hasHtml && !loading && (!!error || (!!data && !(entry === null || entry === void 0 ? void 0 : entry.content.html))),
    };
};
